import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  boundsFor,
  formatDate,
  useDateFormatPref,
  type DateFormatPref,
  type DateRangeMeaning,
} from '../../lib/date';
import { ABSENT } from '../../lib/vocabulary';
import { formatDateTokens, formatNumber } from '../../lib/format';

export interface DateFieldProps {
  label?: string;
  name: string;
  /** ISO `yyyy-MM-dd` (storage format) or '' for empty. */
  value: string;
  /** Receives the assembled ISO value ('' while any part is unselected). */
  onChange: (value: string) => void;
  /**
   * What the field means, which is where its selectable years come from — see
   * `DateRangeMeaning`. Defaults to 'happened', the common case: nearly every
   * date in this app records something that already occurred.
   */
  range?: DateRangeMeaning;
  /** ISO lower bound. Overrides `range` where one field bounds another. */
  min?: string;
  /** ISO upper bound. Overrides `range` where one field bounds another. */
  max?: string;
  required?: boolean;
  disabled?: boolean;
  /** External error (e.g. form-level "required"). Takes precedence over the
   *  component's own bounds message. */
  error?: string;
  /** Optional extra hint shown above the live echo line. */
  helperText?: string;
  className?: string;
}

// Abbreviated month names keep the three-select row compact on mobile while
// still being unambiguous ("Mar", never "03"). Values stay ISO-padded ('03').
// The names come from date-fns ('MMM') in the current language.
const MONTH_INDEXES = Array.from({ length: 12 }, (_, i) => i);

function monthName(index: number): string {
  return formatDateTokens(new Date(2000, index, 1), 'MMM');
}

type Part = 'day' | 'month' | 'year';

// Segment order follows the user's date-format preference. Written-month
// (default) is day-first like the intl numeric form.
const PART_ORDER: Record<DateFormatPref, Part[]> = {
  mdy: ['month', 'day', 'year'],
  dmy: ['day', 'month', 'year'],
  ymd: ['year', 'month', 'day'],
  'dd-mon-yyyy': ['day', 'month', 'year'],
};

interface Parts {
  year: string;
  month: string;
  day: string;
}

const EMPTY_PARTS: Parts = { year: '', month: '', day: '' };

function parseParts(value: string | null | undefined): Parts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').slice(0, 10));
  if (!match) return EMPTY_PARTS;
  return { year: match[1], month: match[2], day: match[3] };
}

/** Days in a given month. When the year is unknown, assume a leap year so the
 *  29th of February stays selectable until a non-leap year is actually chosen. */
function daysInMonth(year: string, month: string): number {
  if (!month) return 31;
  const y = year ? Number(year) : 2000; // 2000 is a leap year
  return new Date(y, Number(month), 0).getDate();
}

/** Assemble the three parts into an ISO date, or '' if incomplete/impossible. */
function composeISO(parts: Parts): string {
  const { year, month, day } = parts;
  if (!year || !month || !day) return '';
  if (Number(day) > daysInMonth(year, month)) return '';
  return `${year}-${month}-${day}`;
}

/**
 * A date input built from three explicit selects — day, month (as names), and
 * year — instead of a native `<input type="date">`.
 *
 * Why: the native control renders in the browser locale with no format label, so
 * "09/07/2026" reads as Sep 7 to some and Jul 9 to others; and typing a full date
 * lands in a single segment, which testers found fiddly. It also silently coerces
 * any incomplete/invalid typed entry to "" — the confirmed mechanism that lost a
 * tester's DOB. Explicit selects make every part unambiguous, make a mis-parse
 * structurally impossible (you can only pick real values), and constrain the day
 * list to the chosen month/year so "31 February" can never be entered.
 *
 * The external contract is unchanged: ISO `yyyy-MM-dd` value/onChange (''
 * when any part is unset), min/max bounds with written-month error copy that
 * takes second place to an external `error`, and an explicit "No date entered"
 * empty state. Segment order follows the user's date-format preference.
 */
export const DateField: React.FC<DateFieldProps> = ({
  label,
  name,
  value,
  onChange,
  range = 'happened',
  min,
  max,
  required = false,
  disabled = false,
  error,
  helperText,
  className,
}) => {
  const { t } = useTranslation();
  const pref = useDateFormatPref();
  const [parts, setParts] = useState<Parts>(() => parseParts(value));

  // Keep the selects in sync when the external value diverges from what they
  // already represent. Comparing against the composed value (not the raw prop)
  // means an in-progress partial selection — which composes to '' and pushes ''
  // up — is NOT wiped when the controlled parent echoes that '' back.
  useEffect(() => {
    setParts((prev) => (composeISO(prev) === (value ?? '') ? prev : parseParts(value)));
  }, [value]);

  const commit = (next: Parts): void => {
    // Clamp the day to the chosen month/year so switching 31 → February can't
    // leave an impossible date selected.
    const maxDay = daysInMonth(next.year, next.month);
    const clamped =
      next.day && Number(next.day) > maxDay
        ? { ...next, day: String(maxDay).padStart(2, '0') }
        : next;
    setParts(clamped);
    onChange(composeISO(clamped));
  };

  // The window comes from what the field MEANS (`range`). An explicit min/max
  // bounds VALIDATION where one field bounds another — "Through" cannot precede
  // "Show data since" — but it does not narrow the years on offer.
  //
  // It used to do both, and that is why the two halves of the vet-prep row
  // disagreed: "Through" carries min={since}, which collapsed its year list to
  // the single year the "since" date fell in, against twenty-one on the field
  // beside it. A period spanning a year boundary could not be expressed at all.
  // Selecting a year outside the bounds is still refused — in words, below the
  // control — rather than by making it unreachable.
  const meaningBounds = boundsFor(range);
  const effectiveMin = min ?? meaningBounds.min;
  const effectiveMax = max ?? meaningBounds.max;
  const windowMaxYear = Number(meaningBounds.max.slice(0, 4));
  const windowMinYear = Number(meaningBounds.min.slice(0, 4));

  const yearOptions = useMemo(() => {
    const set = new Set<number>();
    for (let y = windowMaxYear; y >= windowMinYear; y--) set.add(y);
    // Always include the current value's year, even if it falls outside the
    // computed window (e.g. legacy data), so it renders correctly.
    if (parts.year) set.add(Number(parts.year));
    return Array.from(set).sort((a, b) => b - a);
  }, [windowMinYear, windowMaxYear, parts.year]);

  const dayOptions = useMemo(() => {
    const count = daysInMonth(parts.year, parts.month);
    return Array.from({ length: count }, (_, i) => i + 1);
  }, [parts.year, parts.month]);

  // App-level bounds validation using written-month dates.
  const composed = composeISO(parts);
  let boundsError: string | undefined;
  if (composed) {
    if (composed > effectiveMax) {
      boundsError = t('shared.dateField.afterMax', { date: formatDate(effectiveMax) });
    } else if (composed < effectiveMin) {
      boundsError = t('shared.dateField.beforeMin', { date: formatDate(effectiveMin) });
    }
  }
  const effectiveError = error ?? boundsError;

  const labelText = label ?? name;
  const selectClass = `min-w-0 px-3 py-2.5 text-sm rounded-xl border bg-white transition-all duration-200
    disabled:bg-sage-50 disabled:text-gray-600 disabled:cursor-not-allowed focus:outline-none min-h-[44px]
    ${effectiveError
      ? 'border-danger-400 focus:border-danger-500 focus:shadow-glow-danger'
      : 'border-faint hover:border-gray-300 focus:border-sage-500 focus:shadow-glow'}`;

  const selects: Record<Part, React.ReactNode> = {
    day: (
      <select
        aria-label={t('shared.dateField.dayAria', { label: labelText })}
        aria-invalid={Boolean(effectiveError)}
        className={`${selectClass} flex-1`}
        disabled={disabled}
        value={parts.day}
        onChange={(e) => commit({ ...parts, day: e.target.value })}
      >
        <option value="">{t('shared.dateField.dayPlaceholder')}</option>
        {dayOptions.map((d) => (
          <option key={d} value={String(d).padStart(2, '0')}>
            {formatNumber(d)}
          </option>
        ))}
      </select>
    ),
    month: (
      <select
        aria-label={t('shared.dateField.monthAria', { label: labelText })}
        aria-invalid={Boolean(effectiveError)}
        className={`${selectClass} flex-[1.4]`}
        disabled={disabled}
        value={parts.month}
        onChange={(e) => commit({ ...parts, month: e.target.value })}
      >
        <option value="">{t('shared.dateField.monthPlaceholder')}</option>
        {MONTH_INDEXES.map((i) => (
          <option key={i} value={String(i + 1).padStart(2, '0')}>
            {monthName(i)}
          </option>
        ))}
      </select>
    ),
    year: (
      <select
        aria-label={t('shared.dateField.yearAria', { label: labelText })}
        aria-invalid={Boolean(effectiveError)}
        className={`${selectClass} flex-1`}
        disabled={disabled}
        value={parts.year}
        onChange={(e) => commit({ ...parts, year: e.target.value })}
      >
        <option value="">{t('shared.dateField.yearPlaceholder')}</option>
        {yearOptions.map((y) => (
          <option key={y} value={String(y)}>
            {formatNumber(y)}
          </option>
        ))}
      </select>
    ),
  };

  const order = PART_ORDER[pref] ?? PART_ORDER['dd-mon-yyyy'];

  return (
    <div className={className}>
      {label && (
        <span className="block text-sm font-medium text-charcoal mb-1.5">
          {label}
          {required && <span className="text-danger-500 ml-0.5">*</span>}
        </span>
      )}

      <div className="flex gap-2" role="group" aria-label={labelText}>
        {order.map((part) => (
          <React.Fragment key={part}>{selects[part]}</React.Fragment>
        ))}
      </div>

      {effectiveError && (
        <p className="mt-1.5 text-sm text-danger-600 font-medium animate-fade-in" role="alert">
          {effectiveError}
        </p>
      )}

      {helperText && !effectiveError && (
        <p className="mt-1.5 text-xs text-mid">{helperText}</p>
      )}

      {/* Live echo line — the assembled date in the owner's own format, or an
          explicit empty state so a blank field never reads as a saved one. It
          used to be pinned to written-month, which made it the one line on the
          screen that ignored the setting. It can follow the setting safely now:
          the month is picked BY NAME above, so nothing here can be mis-parsed. */}
      {composed ? (
        <p className="mt-1.5 text-xs text-mid">= {formatDate(composed)}</p>
      ) : (
        <p className="mt-1.5 text-xs text-mid">{ABSENT.label}</p>
      )}
    </div>
  );
};

export default DateField;
